import { useState, useEffect } from "react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useLeaveRequestValidation } from "@/hooks/leave/useLeaveRequestValidation";
import { useLeaveBalances } from "@/hooks/leave/useLeaveBalances";
import { useAuth } from "@/hooks/useAuth";
import { useHasPermission } from "@/hooks/permissions/useHasPermission";
import { formatLeaveDuration, isPartDayRequest, getRequestedDays } from "@/utils/leaveDurationUtils";
import { leaveService } from "@/services/leave/leaveService";
import { LeaveConflictResolutionModal } from "@/components/leave/LeaveConflictResolutionModal";
import type { ShiftConflict } from "@/services/leave/conflictTypes";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Clock,
  ShieldAlert,
  Loader2,
} from "lucide-react";

interface LeaveRequestWithProfile {
  id: string;
  profile_id: string;
  company_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  reason: string | null;
  status: string;
  created_at: string;
  requested_hours?: number | null;
  requested_days?: number | null;
  profile: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  };
}

interface ManagerApprovalDialogProps {
  request: LeaveRequestWithProfile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (requestId: string, overrideReason?: string) => void;
  onReject: (requestId: string, reason?: string) => void;
}

export function ManagerApprovalDialog({
  request,
  open,
  onOpenChange,
  onApprove,
  onReject,
}: ManagerApprovalDialogProps) {
  // ALL HOOKS MUST BE AT THE TOP - before any conditional returns
  const { profile } = useAuth();
  const { toast } = useToast();
  
  // Check if manager can override policies using permission system
  // MOVED HERE: Must be called before early return to comply with Rules of Hooks
  const canOverride = useHasPermission('approve_leave_requests');
  
  // State hooks
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [overridePolicy, setOverridePolicy] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  
  // Conflict resolution state (Phase 6)
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [conflicts, setConflicts] = useState<ShiftConflict[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Get validation data for the request (handles null gracefully with defaults)
  const validation = useLeaveRequestValidation(
    request?.leave_type || "",
    request?.start_date || "",
    request?.end_date || ""
  );

  // Get balance data for the employee (handles undefined profile_id)
  const { data: employeeBalance } = useLeaveBalances(request?.profile_id);

  // Reset state when dialog opens/closes or request changes
  useEffect(() => {
    if (!open) {
      setDecision(null);
      setOverridePolicy(false);
      setOverrideReason("");
      setRejectReason("");
      setConflicts([]);
      setShowConflictModal(false);
      setIsCheckingConflicts(false);
    }
  }, [open, request?.id]);

  // EARLY RETURN - After all hooks are called
  if (!request) return null;

  // Derived values (not hooks, can be after early return)
  const isPartDay = isPartDayRequest(request);
  const requestedDays = getRequestedDays(request);
  const employeeName = `${request.profile?.first_name || 'Unknown'} ${request.profile?.last_name || 'User'}`;
  const employeeInitials = `${request.profile?.first_name?.[0] || 'U'}${request.profile?.last_name?.[0] || 'U'}`;

  // Find current balance for this leave type
  const currentBalance = employeeBalance?.find(b => b.policy.leave_type === request.leave_type);
  const availableBalance = currentBalance?.current_balance_days || 0;
  const balanceAfterApproval = availableBalance - requestedDays;
  
  // Determine if policy override is needed
  const hasValidationErrors = validation.errors.some(e => e.severity === 'error');
  const needsOverride = hasValidationErrors || balanceAfterApproval < 0;

  // Can approve without override if no errors
  const canApproveDirectly = !hasValidationErrors;
  
  // Can approve with override if manager has permissions
  const canApproveWithOverride = needsOverride && canOverride && overridePolicy && overrideReason.trim();

  const handleConfirm = async () => {
    if (!request) return;

    if (decision === 'approve') {
      // Check for conflicts before approving (F9 mitigation)
      setIsCheckingConflicts(true);
      try {
        const foundConflicts = await leaveService.checkLeaveConflicts(
          request.profile_id,
          request.start_date,
          request.end_date,
          request.start_time,
          request.end_time
        );
        
        if (foundConflicts.length > 0) {
          setConflicts(foundConflicts);
          setShowConflictModal(true);
          return; // Don't close dialog - show conflict modal
        }
        
        // No conflicts - proceed with existing approval
        if (needsOverride && canOverride) {
          onApprove(request.id, overrideReason);
        } else {
          onApprove(request.id);
        }
      } catch (error) {
        console.error('Error checking conflicts:', error);
        // Fail closed: do NOT approve if conflict check fails
        toast({
          title: 'Conflict check failed',
          description: 'Unable to verify schedule conflicts. Please try again.',
          variant: 'destructive',
        });
        return;
      } finally {
        setIsCheckingConflicts(false);
      }
    } else if (decision === 'reject') {
      onReject(request.id, rejectReason);
    }
    
    onOpenChange(false);
  };
  
  // Handle successful conflict resolution
  const handleConflictResolutionSuccess = () => {
    setShowConflictModal(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Leave Request</DialogTitle>
          <DialogDescription>
            Review policy compliance and balance impact before making a decision
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Employee Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" />
                Employee Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={request.profile?.avatar_url || ''} alt={employeeName} />
                  <AvatarFallback>{employeeInitials}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{employeeName}</p>
                  <p className="text-sm text-muted-foreground">{request.profile?.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Request Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="h-4 w-4" />
                Request Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Leave Type</Label>
                  <p className="font-medium">{request.leave_type}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Duration</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{formatLeaveDuration(request)}</p>
                    {isPartDay && (
                      <Badge variant="secondary" className="text-xs">Part Day</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Start Date</Label>
                  <p>{format(new Date(request.start_date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">End Date</Label>
                  <p>{format(new Date(request.end_date), 'MMM d, yyyy')}</p>
                </div>
              </div>
              
              {request.reason && (
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Reason</Label>
                  <p className="text-sm mt-1">{request.reason}</p>
                </div>
              )}
              
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Submitted</Label>
                <p className="text-sm">{format(new Date(request.created_at), 'MMM d, yyyy \'at\' h:mm a')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Balance Impact */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4" />
                Balance Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs font-medium text-muted-foreground">Current Balance</Label>
                  <p className="text-lg font-semibold">{availableBalance} days</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs font-medium text-muted-foreground">Requested</Label>
                  <p className="text-lg font-semibold text-orange-600">
                    {isPartDay ? `${request.requested_hours}h` : `${requestedDays} days`}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs font-medium text-muted-foreground">After Approval</Label>
                  <p className={`text-lg font-semibold ${balanceAfterApproval < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {balanceAfterApproval.toFixed(2)} days
                  </p>
                </div>
              </div>
              
              {balanceAfterApproval < 0 && (
                <Alert className="mt-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    This request would result in a negative balance of {Math.abs(balanceAfterApproval).toFixed(2)} days.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Policy Validation */}
          {(validation.errors.length > 0 || validation.warnings.length > 0) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldAlert className="h-4 w-4" />
                  Policy Validation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {validation.errors.map((error, index) => (
                  <Alert key={index} variant={error.severity === 'error' ? 'destructive' : 'default'}>
                    {error.severity === 'error' ? (
                      <XCircle className="h-4 w-4" />
                    ) : (
                      <AlertTriangle className="h-4 w-4" />
                    )}
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                ))}
                
                {validation.warnings.map((warning, index) => (
                  <Alert key={`warning-${index}`}>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{warning}</AlertDescription>
                  </Alert>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Decision Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Decision</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={decision === 'approve' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setDecision('approve')}
                  disabled={needsOverride && !canOverride}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant={decision === 'reject' ? 'destructive' : 'outline'}
                  className="flex-1"
                  onClick={() => setDecision('reject')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>

              {/* Override section for policy violations */}
              {decision === 'approve' && needsOverride && canOverride && (
                <div className="space-y-3 p-4 border rounded-lg bg-orange-50 dark:bg-orange-950/20">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="override-policy"
                      checked={overridePolicy}
                      onCheckedChange={(checked) => setOverridePolicy(checked as boolean)}
                    />
                    <Label htmlFor="override-policy" className="text-sm font-medium">
                      Override policy restrictions
                    </Label>
                  </div>
                  
                  {overridePolicy && (
                    <div>
                      <Label htmlFor="override-reason" className="text-sm">Override Reason (Required)</Label>
                      <Textarea
                        id="override-reason"
                        placeholder="Explain why you're overriding policy restrictions..."
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Rejection reason */}
              {decision === 'reject' && (
                <div>
                  <Label htmlFor="reject-reason" className="text-sm">Rejection Reason (Required)</Label>
                  <Textarea
                    id="reject-reason"
                    placeholder="Provide feedback for the employee..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              !decision ||
              (decision === 'approve' && needsOverride && !canApproveWithOverride) ||
              (decision === 'reject' && !rejectReason.trim()) ||
              validation.isValidating ||
              isCheckingConflicts
            }
            variant={decision === 'reject' ? 'destructive' : 'default'}
          >
            {isCheckingConflicts ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking conflicts...
              </>
            ) : validation.isValidating ? 'Validating...' : `Confirm ${decision === 'approve' ? 'Approval' : 'Rejection'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* Conflict Resolution Modal (Phase 6) */}
      {request && (
        <LeaveConflictResolutionModal
          open={showConflictModal}
          onClose={() => setShowConflictModal(false)}
          conflicts={conflicts}
          leaveRequest={{
            id: request.id,
            profile_id: request.profile_id,
            company_id: request.company_id,
            start_date: request.start_date,
            end_date: request.end_date,
            leave_type: request.leave_type,
            profile: request.profile,
          }}
          onSuccess={handleConflictResolutionSuccess}
        />
      )}
    </Dialog>
  );
}
